import { useState } from "react";
import { router } from "@inertiajs/react";
import styled from "styled-components";
import { Loader } from "lucide-react";
import { PageHeader } from "../../../../components/page-header";
import { useLanguage } from "../../../../contexts/language";
import { useNotification } from "../../../../contexts/notification";
import { useOrder } from "../../../../contexts/order";
import { useRoom } from "../../../../contexts/room";
import { saveOrderToLocalHistory } from "../../../../util";
import { getKitchenStatus } from "../fnb/mock";

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f9f9f9;
`;

const EmptyStateContainer = styled.div`
  min-height: 100vh;
  background-color: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyStateContent = styled.div`
  text-align: center;
`;

const EmptyStateText = styled.p`
  color: #666;
  margin-bottom: 16px;
`;

const EmptyStateButton = styled.button`
  padding: 8px 16px;
  background-color: #c9a961;
  color: #fff;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(201, 169, 97, 0.9);
  }
`;

const MainContent = styled.div`
  margin: 0 auto;
  max-width: 768px;
  padding: 32px 16px;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 24px;
  background-color: #fff;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const OrderItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 256px;
  overflow-y: auto;
`;

const OrderItemThumbnail = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
`;

const OrderItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
`;

const OrderItemName = styled.span`
  color: #666;
`;

const OrderItemPrice = styled.span`
  color: #333;
  font-weight: 500;
`;

const ItemNoteInput = styled.textarea`
  margin-top: 6px;
  width: 100%;
  min-height: 52px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  color: #555;
  resize: vertical;
`;

const PaymentMethodsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PaymentMethodLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  input[type="radio"] {
    width: 16px;
    height: 16px;
  }

  span {
    color: #333;
    font-weight: 500;
  }
`;

const CardForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormField = styled.div``;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: calc(100% - 32px);
  padding: 8px 16px;
  background-color: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  color: #333;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: #c9a961;
    box-shadow: 0 0 0 2px rgba(201, 169, 97, 0.1);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const RightColumn = styled.div``;

const StickyCard = styled(Card)`
  position: sticky;
  top: 96px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 14px;
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SummaryLabel = styled.span`
  color: #666;
`;

const SummaryValue = styled.span`
  color: #333;
  font-weight: 500;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: #333;
`;

const TotalValue = styled.span`
  font-size: 24px;
  font-weight: 600;
  color: #c9a961;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #c9a961;
  color: #fff;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: rgba(201, 169, 97, 0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const CancelButton = styled.button`
  width: 100%;
  padding: 8px;
  border: 1px solid #e5e5e5;
  color: #333;
  background-color: #fff;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default function CheckoutPage(props) {
  const { building_code } = props;
  const { t } = useLanguage();
  const { subscribe, getSubscriptionId } = useNotification();
  const { cart, getCartTotal, processPayment, isProcessing, updateCartItemNote } = useOrder();
  const { roomNumber, guestName } = useRoom();
  const [userName, setUserName] = useState(guestName || "");

  if (cart.length === 0) {
    return (
      <EmptyStateContainer>
        <EmptyStateContent>
          <EmptyStateText>{t.order.cart.empty}</EmptyStateText>
          <EmptyStateButton onClick={() => window.history.back()}>
            {t.order.backButton}
          </EmptyStateButton>
        </EmptyStateContent>
      </EmptyStateContainer>
    );
  }

  const TAX_RATE = 0;
  const subtotal = getCartTotal();
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;
  const formatAmount = (amount) => `Rp${Math.round(Number(amount || 0)).toLocaleString("id-ID")}`;

  const handlePayment = async () => {
    try {
      const kitchenStatus = await getKitchenStatus();
      if (kitchenStatus && !kitchenStatus.is_open) {
        window.alert(t.kitchen.closedMessage);
        return;
      }
      const webPushSubscriptionId = getSubscriptionId() ?? (await subscribe());
      const order = await processPayment({
        userName,
        buildingCode: building_code,
        roomNumber: roomNumber || undefined,
        webPushSubscriptionId: webPushSubscriptionId || undefined,
      });
      if (order?.id) {
        saveOrderToLocalHistory({ buildingCode: building_code, order });
        router.visit(`/hebe/${building_code}/hgs/orders/${order.id}?auto_pay=1`);
      }
    } catch (error) {
      window.alert(error?.message || "Failed to create order");
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title={t.order.payment.title}
        maxWidth="768px"
      />

      <MainContent>
        <ContentGrid>
          <LeftColumn>
            <Card>
              <CardTitle>{t.order.payment.orderSummary}</CardTitle>
              <OrderItemsContainer>
                {cart.map((item) => (
                  <OrderItem key={item.id}>
                    {item.image && <OrderItemThumbnail src={item.image} alt={item.name} />}
                    <div style={{ flex: 1 }}>
                      <OrderItemName>
                        {item.name} x {item.quantity}
                      </OrderItemName>
                      <ItemNoteInput
                        value={item.note || ""}
                        onChange={(event) => updateCartItemNote(item.id, event.target.value)}
                        maxLength={200}
                        placeholder={t.order.itemNotePlaceholder}
                      />
                    </div>
                    <OrderItemPrice>
                      {formatAmount(Number(item?.price?.amount || 0) * item.quantity)}
                    </OrderItemPrice>
                  </OrderItem>
                ))}
              </OrderItemsContainer>
            </Card>

            <Card>
              <CardTitle>{t.order.payment.orderDetails}</CardTitle>
              <CardForm>
                <FormField>
                  <FormLabel>{t.order.payment.yourName}</FormLabel>
                  <FormInput
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder={t.order.payment.enterYourName}
                  />
                </FormField>
              </CardForm>
            </Card>
          </LeftColumn>

          <RightColumn>
            <StickyCard>
              <CardTitle>{t.order.cart.total}</CardTitle>
              <SummarySection>
                <SummaryRow>
                  <SummaryLabel>{t.order.cart.subtotal}</SummaryLabel>
                  <SummaryValue>
                    {formatAmount(subtotal)}
                  </SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>{t.order.cart.tax}</SummaryLabel>
                  <SummaryValue>{formatAmount(tax)}</SummaryValue>
                </SummaryRow>
              </SummarySection>
              <TotalRow>
                <TotalLabel>{t.order.cart.total}</TotalLabel>
                <TotalValue>{formatAmount(total)}</TotalValue>
              </TotalRow>

              <ConfirmButton onClick={handlePayment} disabled={isProcessing}>
                {isProcessing && <Loader size={16} />}
                {isProcessing
                  ? t.order.payment.processing
                  : t.order.payment.confirm}
              </ConfirmButton>

              <CancelButton
                onClick={() => window.history.back()}
                disabled={isProcessing}
              >
                {t.order.payment.cancel}
              </CancelButton>
            </StickyCard>
          </RightColumn>
        </ContentGrid>
      </MainContent>
    </PageContainer>
  );
}
